import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  PictureAsPdf as PdfIcon,
  Block as BlockIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  BookmarkBorder as BookmarkIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { projectsAPI, reportsAPI, timeEntriesAPI, usersAPI, customersAPI, invoicesAPI } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ReportsPage = () => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [reassigningEntry, setReassigningEntry] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentValue, setEditCommentValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Saved reports state
  const [savedReports, setSavedReports] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [reportName, setReportName] = useState('');

  // Invoice creation state
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [invoiceFormData, setInvoiceFormData] = useState({
    customer_id: '',
    project_id: '',
    date_from: '',
    date_to: '',
    tax_rate: 19,
    payment_terms: 'Zahlbar innerhalb von 14 Tagen ohne Abzug.',
  });

  const [filters, setFilters] = useState({
    customer_id: '',
    project_id: '',
    user_id: '',
    start_date: '',
    end_date: '',
  });

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    fetchSavedReports();
    fetchCustomers();
  }, []);

  // Vom Dashboard („Abrechnung Vormonat"): Kunde + Zeitraum vorbelegt, Bericht sofort laden
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    navigate(location.pathname, { replace: true, state: {} });
    setFilters({
      customer_id: prefill.customer_id || '',
      project_id: '',
      user_id: '',
      start_date: prefill.start_date,
      end_date: prefill.end_date,
    });
    setLoading(true);
    setReportData(null);
    reportsAPI.getData({ customer_id: prefill.customer_id, date_from: prefill.start_date, date_to: prefill.end_date })
      .then((res) => {
        setReportData(res.data);
        setSuccess(`Bericht ${prefill.label || ''} geladen`.replace('  ', ' '));
      })
      .catch((err) => setError(err.response?.data?.error || 'Fehler beim Laden des Berichts'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.prefill]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Projekte');
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer:', err);
    }
  };

  const fetchSavedReports = async () => {
    try {
      const response = await reportsAPI.getSaved();
      setSavedReports(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der gespeicherten Berichte:', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
    }
  };

  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Mindestens ein Filter: Kunde, Projekt ODER Benutzer (reiner Benutzer-Report)
    if (!filters.customer_id && !filters.project_id && !filters.user_id) {
      setError('Bitte wählen Sie mindestens einen Kunden, ein Projekt oder einen Benutzer aus');
      return;
    }

    setLoading(true);
    setReportData(null);

    try {
      // Build params object with only non-empty values
      const params = {
        date_from: filters.start_date,
        date_to: filters.end_date,
      };
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.user_id) params.user_id = filters.user_id;

      const response = await reportsAPI.getData(params);
      setReportData(response.data);
      setSuccess('Bericht erfolgreich generiert');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Generieren des Berichts');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setError('');
    setDownloadingPDF(true);

    try {
      // Build params object with only non-empty values
      const params = {
        date_from: filters.start_date,
        date_to: filters.end_date,
      };
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.user_id) params.user_id = filters.user_id;

      // Find the customer, project and user names for the report title
      const customer = customers.find(c => c.id === parseInt(filters.customer_id));
      const project = projects.find(p => p.id === parseInt(filters.project_id));
      const filterUser = users.find(u => u.id === parseInt(filters.user_id));

      let reportName = 'Zeiterfassung';
      if (customer) reportName += ` ${customer.company_name}`;
      if (project) reportName += ` - ${project.name}`;
      if (filterUser) reportName += ` - ${filterUser.username}`;

      params.report_name = reportName;

      const response = await reportsAPI.downloadPDF(params);

      // Create a blob from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create a link element and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      let filename = 'Zeiterfassung';
      if (customer) filename += `_${customer.company_name.replace(/[^a-z0-9]/gi, '_')}`;
      if (project) filename += `_${project.name.replace(/[^a-z0-9]/gi, '_')}`;
      if (filterUser) filename += `_${filterUser.username.replace(/[^a-z0-9]/gi, '_')}`;
      filename += `_${filters.start_date}_${filters.end_date}.pdf`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('PDF erfolgreich heruntergeladen');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Herunterladen des PDFs');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateTotalHours = () => {
    if (!reportData || !reportData.tasks) return '0.00';

    return reportData.tasks.reduce((taskSum, task) => {
      const taskTotal = task.subtasks.reduce((subtaskSum, subtask) =>
        subtaskSum + (parseFloat(subtask.hours) || 0), 0
      );
      return taskSum + taskTotal;
    }, 0).toFixed(2);
  };

  const handleEditClick = (entryId, currentHours) => {
    setEditingEntry(entryId);
    setEditValue(currentHours.toString());
  };

  const handleEditCancel = () => {
    setEditingEntry(null);
    setEditValue('');
  };

  const handleEditSave = async (entryId) => {
    const newHours = parseFloat(editValue);

    if (isNaN(newHours) || newHours < 0) {
      setError('Bitte geben Sie eine gültige Stundenanzahl ein (mindestens 0)');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Update via API
      await timeEntriesAPI.update(entryId, { hours: newHours });

      // Update local state
      setReportData(prevData => {
        const newData = { ...prevData };
        newData.tasks = newData.tasks.map(task => ({
          ...task,
          subtasks: task.subtasks.map(subtask =>
            subtask.id === entryId
              ? { ...subtask, hours: newHours }
              : subtask
          ),
        }));
        return newData;
      });

      setSuccess('Stunden erfolgreich aktualisiert');
      setEditingEntry(null);
      setEditValue('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren der Stunden');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, entryId) => {
    if (e.key === 'Enter') {
      handleEditSave(entryId);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // Zeit auf eine andere Person umbuchen (Abrechnung)
  const handleReassign = async (entryId, newUserId) => {
    setSaving(true);
    setError('');
    try {
      await timeEntriesAPI.update(entryId, { user_id: parseInt(newUserId) });
      const u = users.find((x) => x.id === parseInt(newUserId));
      setReportData((prevData) => {
        const newData = { ...prevData };
        newData.tasks = newData.tasks.map((task) => ({
          ...task,
          subtasks: task.subtasks.map((subtask) =>
            subtask.id === entryId
              ? { ...subtask, user_id: parseInt(newUserId), username: u ? u.username : subtask.username }
              : subtask
          ),
        }));
        return newData;
      });
      setSuccess('Zeit auf eine andere Person umgebucht');
      setReassigningEntry(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Umbuchen');
    } finally {
      setSaving(false);
    }
  };

  const handleCommentEditClick = (entryId, currentComment) => {
    setEditingComment(entryId);
    setEditCommentValue(currentComment || '');
  };

  const handleCommentEditCancel = () => {
    setEditingComment(null);
    setEditCommentValue('');
  };

  const handleCommentEditSave = async (entryId) => {
    if (!editCommentValue.trim()) {
      setError('Bitte geben Sie eine Beschreibung ein');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await timeEntriesAPI.update(entryId, { subtask_comment: editCommentValue.trim() });

      setReportData(prevData => {
        const newData = { ...prevData };
        newData.tasks = newData.tasks.map(task => ({
          ...task,
          subtasks: task.subtasks.map(subtask =>
            subtask.id === entryId
              ? { ...subtask, subtask_comment: editCommentValue.trim() }
              : subtask
          ),
        }));
        return newData;
      });

      setSuccess('Beschreibung erfolgreich aktualisiert');
      setEditingComment(null);
      setEditCommentValue('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren der Beschreibung');
    } finally {
      setSaving(false);
    }
  };

  const handleCommentKeyDown = (e, entryId) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommentEditSave(entryId);
    } else if (e.key === 'Escape') {
      handleCommentEditCancel();
    }
  };

  const handleLoadSavedReport = async (savedReport) => {
    // Set filters from saved report
    setFilters({
      customer_id: savedReport.customer_id || '',
      project_id: savedReport.project_id || '',
      user_id: '',
      start_date: savedReport.start_date,
      end_date: savedReport.end_date,
    });

    // Generate report automatically
    setError('');
    setSuccess('');
    setLoading(true);
    setReportData(null);

    try {
      const params = {
        date_from: savedReport.start_date,
        date_to: savedReport.end_date,
      };
      if (savedReport.customer_id) params.customer_id = savedReport.customer_id;
      if (savedReport.project_id) params.project_id = savedReport.project_id;

      const response = await reportsAPI.getData(params);
      setReportData(response.data);
      setSuccess(`Gespeicherter Bericht "${savedReport.name}" wurde geladen`);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Laden des gespeicherten Berichts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSaveDialog = () => {
    if ((!filters.customer_id && !filters.project_id) || !filters.start_date || !filters.end_date) {
      setError('Bitte wählen Sie mindestens einen Kunden oder ein Projekt und geben Sie einen Zeitraum an');
      return;
    }
    setSaveDialogOpen(true);
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      setError('Bitte geben Sie einen Namen für den Bericht ein');
      return;
    }

    try {
      const data = {
        name: reportName,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };
      if (filters.customer_id) data.customer_id = filters.customer_id;
      if (filters.project_id) data.project_id = filters.project_id;

      await reportsAPI.saveReport(data);

      setSuccess('Bericht erfolgreich gespeichert');
      setSaveDialogOpen(false);
      setReportName('');
      fetchSavedReports();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Berichts');
    }
  };

  const handleDeleteSavedReport = async (reportId) => {
    if (!window.confirm('Möchten Sie diesen gespeicherten Bericht wirklich löschen?')) {
      return;
    }

    try {
      await reportsAPI.deleteSaved(reportId);
      setSuccess('Gespeicherter Bericht wurde gelöscht');
      fetchSavedReports();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen des gespeicherten Berichts');
    }
  };

  const handleCreateInvoice = () => {
    // Pre-fill invoice form with report filters
    setInvoiceFormData({
      customer_id: '',
      project_id: filters.project_id || '',
      date_from: filters.start_date || '',
      date_to: filters.end_date || '',
      tax_rate: 19,
      payment_terms: 'Zahlbar innerhalb von 14 Tagen ohne Abzug.',
    });
    setShowInvoiceDialog(true);
  };

  const handleInvoiceDialogClose = () => {
    setShowInvoiceDialog(false);
  };

  const handleInvoiceFormChange = (e) => {
    setInvoiceFormData({
      ...invoiceFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCreateInvoiceFromEntries = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = {
        customer_id: parseInt(invoiceFormData.customer_id),
        project_id: invoiceFormData.project_id ? parseInt(invoiceFormData.project_id) : null,
        date_from: invoiceFormData.date_from,
        date_to: invoiceFormData.date_to,
        tax_rate: parseFloat(invoiceFormData.tax_rate),
        payment_terms: invoiceFormData.payment_terms,
      };

      const response = await invoicesAPI.createFromTimeEntries(data);
      const skipped = response.data.skipped_entry_count;
      setSuccess(`Rechnung #${response.data.invoice_number} erfolgreich erstellt`
        + (skipped ? ` (${skipped} bereits abgerechnete Einträge übersprungen)` : ''));
      setShowInvoiceDialog(false);

      // Navigate to invoices page after 1.5 seconds
      setTimeout(() => {
        navigate('/invoices');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen der Rechnung');
    }
  };

  if (user.role !== 'admin') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <BlockIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: '#e01e26' }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Zugriff verweigert
              </Typography>
              <Typography variant="body1" color="error.main">
                Sie haben keine Berechtigung, auf diese Seite zuzugreifen. Admin-Rechte erforderlich.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Berichte
      </Typography>

      {/* Saved Reports Card */}
      {savedReports.length > 0 && (
        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BookmarkIcon sx={{ color: '#1e3a5f' }} />
              <Typography variant="h6" fontWeight={600}>
                Gespeicherte Berichte
              </Typography>
            </Box>

            <List sx={{ p: 0 }}>
              {savedReports.map((report, index) => (
                <ListItem
                  key={report.id}
                  disablePadding
                  sx={{
                    borderTop: index > 0 ? '1px solid #e0e0e0' : 'none',
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteSavedReport(report.id)}
                      sx={{
                        color: '#e01e26',
                        '&:hover': { bgcolor: 'rgba(224, 30, 38, 0.1)' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => handleLoadSavedReport(report)}
                    sx={{
                      py: 2,
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#1e3a5f' }}>
                          {report.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#616161' }}>
                            Projekt: {report.project_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#616161' }}>
                            {formatDate(report.start_date)} - {formatDate(report.end_date)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Filter Card */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Bericht generieren
          </Typography>

          <Box component="form" onSubmit={handleGenerateReport}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                <TextField
                  select
                  fullWidth
                  label="Kunde"
                  name="customer_id"
                  value={filters.customer_id}
                  onChange={handleChange}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 300 }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      minWidth: '200px'
                    }
                  }}
                  helperText="Leer lassen für alle Kunden, oder auswählen für kundenspezifischen Bericht"
                >
                  <MenuItem value="">Alle Kunden</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                <TextField
                  select
                  fullWidth
                  label="Projekt"
                  name="project_id"
                  value={filters.project_id}
                  onChange={handleChange}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 300 }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      minWidth: '200px'
                    }
                  }}
                  helperText="Optional: Leer lassen für alle Projekte des Kunden"
                >
                  <MenuItem value="">Alle Projekte</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                <TextField
                  select
                  fullWidth
                  label="Benutzer"
                  name="user_id"
                  value={filters.user_id}
                  onChange={handleChange}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 300 }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      minWidth: '200px'
                    }
                  }}
                  helperText="Optional: Leer lassen für alle Benutzer"
                >
                  <MenuItem value="">Alle Benutzer</MenuItem>
                  {users.map((usr) => (
                    <MenuItem key={usr.id} value={usr.id}>
                      {usr.username}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Startdatum"
                  name="start_date"
                  type="date"
                  value={filters.start_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Enddatum"
                  name="end_date"
                  type="date"
                  value={filters.end_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ReportIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                  color: 'white',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                  },
                }}
              >
                {loading ? 'Wird generiert...' : 'Bericht generieren'}
              </Button>

              <Button
                variant="outlined"
                onClick={handleOpenSaveDialog}
                disabled={(!filters.customer_id && !filters.project_id) || !filters.start_date || !filters.end_date}
                startIcon={<SaveIcon />}
                sx={{
                  borderColor: '#1e3a5f',
                  color: '#1e3a5f',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    borderColor: '#0a1628',
                    bgcolor: 'rgba(30, 58, 95, 0.05)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                  },
                }}
              >
                Bericht speichern
              </Button>

              {reportData && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleDownloadPDF}
                    disabled={downloadingPDF}
                    startIcon={downloadingPDF ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
                      color: 'white',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 100%)',
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(0, 0, 0, 0.12)',
                      },
                    }}
                  >
                    {downloadingPDF ? 'Wird heruntergeladen...' : 'PDF herunterladen'}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleCreateInvoice}
                    startIcon={<ReceiptIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      fontWeight: 600,
                      px: 3,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      },
                    }}
                  >
                    Rechnung erstellen
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mt: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Report Data */}
      {reportData && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            {/* Report Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {reportData.project_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(filters.start_date)} - {formatDate(filters.end_date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: '#f5f5f5',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#616161', fontWeight: 500 }}>
                    Gesamt:
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#1e3a5f',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      minWidth: '90px',
                      textAlign: 'right',
                    }}
                  >
                    {calculateTotalHours()} h
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleCreateInvoice}
                  startIcon={<ReceiptIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    },
                  }}
                >
                  Rechnung erstellen
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Report Content */}
            {reportData.tasks && reportData.tasks.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  color: 'text.secondary',
                }}
              >
                <ReportIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>
                  Keine Zeiteinträge gefunden
                </Typography>
                <Typography variant="body2">
                  Für das ausgewählte Projekt und den Zeitraum wurden keine Zeiteinträge gefunden.
                </Typography>
              </Box>
            ) : (
              <Box>
                {reportData.tasks.map((task) => (
                  <Box key={task.group_key || task.task_id} sx={{ mb: 4 }}>
                    {/* Task Header - Modern & Clean */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e0e0e0',
                      }}
                    >
                      <Typography variant="h6" fontWeight={600} sx={{ color: '#1e3a5f' }}>
                        {task.task_name}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#e01e26',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          fontVariantNumeric: 'tabular-nums',
                          minWidth: '90px',
                          textAlign: 'right',
                        }}
                      >
                        {task.subtasks.reduce((sum, st) => sum + (parseFloat(st.hours) || 0), 0).toFixed(2)} h
                      </Typography>
                    </Box>

                    {/* Subtasks Table - Modern, Subtle, Clean */}
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden',
                        mb: 3,
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: '#616161',
                              py: 1.5,
                              width: '120px',
                            }}>
                              Datum
                            </TableCell>
                            <TableCell sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: '#616161',
                              py: 1.5,
                            }}>
                              Subtask
                            </TableCell>
                            <TableCell sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: '#616161',
                              py: 1.5,
                              width: '160px',
                            }}>
                              Benutzer
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                color: '#616161',
                                py: 1.5,
                                width: '100px',
                              }}
                            >
                              Stunden
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {task.subtasks.map((subtask, idx) => (
                            <TableRow
                              key={idx}
                              sx={{
                                '&:nth-of-type(odd)': {
                                  bgcolor: '#fafafa',
                                },
                                '&:hover': {
                                  bgcolor: '#f5f5f5',
                                },
                                transition: 'background-color 0.2s ease',
                              }}
                            >
                              <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                                <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.875rem' }}>
                                  {formatDate(subtask.date)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                                {editingComment === subtask.id ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                      value={editCommentValue}
                                      onChange={(e) => setEditCommentValue(e.target.value)}
                                      onKeyDown={(e) => handleCommentKeyDown(e, subtask.id)}
                                      size="small"
                                      fullWidth
                                      autoFocus
                                      disabled={saving}
                                      placeholder="Beschreibung eingeben..."
                                      sx={{
                                        '& input': {
                                          fontSize: '0.875rem',
                                          color: '#424242',
                                        }
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCommentEditSave(subtask.id)}
                                      disabled={saving}
                                      sx={{
                                        color: '#10b981',
                                        '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' }
                                      }}
                                    >
                                      {saving ? <CircularProgress size={16} /> : '✓'}
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleCommentEditCancel}
                                      disabled={saving}
                                      sx={{
                                        color: '#ef4444',
                                        '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                      }}
                                    >
                                      ✕
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Box
                                    onClick={() => handleCommentEditClick(subtask.id, subtask.subtask_comment)}
                                    sx={{
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 0.5,
                                      transition: 'background-color 0.2s ease',
                                      '&:hover': {
                                        bgcolor: '#f0f7ff',
                                      },
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ color: '#424242', flex: 1 }}>
                                      {subtask.subtask_comment}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      component="span"
                                      sx={{
                                        color: '#9ca3af',
                                        fontSize: '0.7rem',
                                        ml: 0.5,
                                        flexShrink: 0,
                                      }}
                                    >
                                      ✎
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                                {reassigningEntry === subtask.id ? (
                                  <TextField
                                    select size="small" autoFocus
                                    value={subtask.user_id || ''}
                                    disabled={saving}
                                    onChange={(e) => handleReassign(subtask.id, e.target.value)}
                                    sx={{ minWidth: 170 }}
                                  >
                                    {users.map((u) => (
                                      <MenuItem key={u.id} value={u.id}>{u.full_name || u.username}</MenuItem>
                                    ))}
                                  </TextField>
                                ) : (
                                  <Box
                                    onClick={() => setReassigningEntry(subtask.id)}
                                    title="Klicken, um die Zeit auf eine andere Person umzubuchen"
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      px: 1.5,
                                      py: 0.5,
                                      bgcolor: '#f5f5f5',
                                      borderRadius: 0.5,
                                      border: '1px solid #e0e0e0',
                                      cursor: 'pointer',
                                      transition: 'all 0.15s ease',
                                      '&:hover': { borderColor: '#1e3a5f', bgcolor: '#eef2f7' },
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ color: '#424242', fontSize: '0.875rem' }}>
                                      {subtask.username}
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                {editingEntry === subtask.id ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
                                    <TextField
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onKeyDown={(e) => handleKeyDown(e, subtask.id)}
                                      type="number"
                                      inputProps={{
                                        step: 0.25,
                                        min: 0,
                                        style: { textAlign: 'right' }
                                      }}
                                      size="small"
                                      autoFocus
                                      disabled={saving}
                                      sx={{
                                        width: '80px',
                                        '& input': {
                                          fontSize: '0.875rem',
                                          fontWeight: 600,
                                          color: '#e01e26',
                                        }
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditSave(subtask.id)}
                                      disabled={saving}
                                      sx={{
                                        color: '#10b981',
                                        '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' }
                                      }}
                                    >
                                      {saving ? <CircularProgress size={16} /> : '✓'}
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleEditCancel}
                                      disabled={saving}
                                      sx={{
                                        color: '#ef4444',
                                        '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' }
                                      }}
                                    >
                                      ✕
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Box
                                    onClick={() => handleEditClick(subtask.id, subtask.hours)}
                                    sx={{
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      gap: 0.5,
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 0.5,
                                      transition: 'background-color 0.2s ease',
                                      whiteSpace: 'nowrap',
                                      minWidth: '80px',
                                      '&:hover': {
                                        bgcolor: '#fff3f3',
                                      },
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      component="span"
                                      sx={{
                                        color: '#e01e26',
                                        fontVariantNumeric: 'tabular-nums',
                                        display: 'inline-block',
                                        minWidth: '70px',
                                        textAlign: 'right',
                                      }}
                                    >
                                      {parseFloat(subtask.hours || 0).toFixed(2)} h
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      component="span"
                                      sx={{
                                        color: '#9ca3af',
                                        fontSize: '0.7rem',
                                        ml: 0.5,
                                      }}
                                    >
                                      ✎
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Save Report Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1e3a5f' }}>
          Bericht speichern
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Berichtsname"
            fullWidth
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSaveReport();
              }
            }}
            sx={{ mt: 2 }}
          />
          <Typography variant="body2" sx={{ mt: 2, color: '#616161' }}>
            Dieser Bericht wird mit folgenden Einstellungen gespeichert:
          </Typography>
          <Box sx={{ mt: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            {filters.customer_id && (
              <Typography variant="body2">
                <strong>Kunde:</strong> {customers.find(c => c.id === parseInt(filters.customer_id))?.company_name || 'N/A'}
              </Typography>
            )}
            {filters.project_id && (
              <Typography variant="body2" sx={{ mt: filters.customer_id ? 0.5 : 0 }}>
                <strong>Projekt:</strong> {projects.find(p => p.id === parseInt(filters.project_id))?.name || 'N/A'}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>Zeitraum:</strong> {filters.start_date} bis {filters.end_date}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => {
              setSaveDialogOpen(false);
              setReportName('');
            }}
            sx={{ color: '#616161' }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSaveReport}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Creation Dialog */}
      <Dialog
        open={showInvoiceDialog}
        onClose={handleInvoiceDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            Rechnung aus Zeiteinträgen erstellen
          </Box>
          <IconButton onClick={handleInvoiceDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="invoice-from-entries-form" onSubmit={handleCreateInvoiceFromEntries}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Es wird eine Rechnung aus den Zeiteinträgen für den gewählten Kunden und Zeitraum erstellt. Optional kann ein bestimmtes Projekt ausgewählt werden, ansonsten werden alle Projekte des Kunden berücksichtigt.
            </Alert>

            <TextField
              select
              fullWidth
              margin="normal"
              label="Kunde"
              name="customer_id"
              value={invoiceFormData.customer_id}
              onChange={handleInvoiceFormChange}
              required
            >
              <MenuItem value="">Kunde auswählen</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.company_name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              margin="normal"
              label="Projekt (optional)"
              name="project_id"
              value={invoiceFormData.project_id}
              onChange={handleInvoiceFormChange}
              helperText="Leer lassen, um alle Projekte des Kunden zu berücksichtigen"
            >
              <MenuItem value="">Alle Projekte des Kunden</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              margin="normal"
              label="Von Datum"
              name="date_from"
              type="date"
              value={invoiceFormData.date_from}
              onChange={handleInvoiceFormChange}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Bis Datum"
              name="date_to"
              type="date"
              value={invoiceFormData.date_to}
              onChange={handleInvoiceFormChange}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Mehrwertsteuersatz (%)"
              name="tax_rate"
              type="number"
              value={invoiceFormData.tax_rate}
              onChange={handleInvoiceFormChange}
              inputProps={{ step: 1, min: 0, max: 100 }}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Zahlungsbedingungen"
              name="payment_terms"
              value={invoiceFormData.payment_terms}
              onChange={handleInvoiceFormChange}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleInvoiceDialogClose}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="invoice-from-entries-form"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            Rechnung erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportsPage;
